import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Icon } from '@/icons';
import { usePreferences } from '@/hooks/usePreferences';
import {
  CATEGORY_LABELS,
  EMPTY_RELEASE_FILTERS,
  PLATFORM_LABELS,
  UPDATE_RELEASES,
  filterReleases,
  type ReleaseCategory,
  type ReleaseFilters,
  type ReleasePlatform,
  type UpdateRelease,
} from '@/core/data/updateLogs';
import { ReleaseBanner } from './ReleaseBanner';
import { ReleaseCard } from './ReleaseHistory';
import { ReleaseSidebar } from './ReleaseSidebar';
import { Control, Muted, Select, Surface, focusRing } from './styles';

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 22px;
  align-items: start;
  @media (min-width: 1650px) {
    grid-template-columns: minmax(0, 1fr) 330px;
  }
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Header = styled.header`
  margin-bottom: 4px;
  & h1 {
    margin: 0 0 7px;
    font: 650 clamp(1.5rem, 2vw, 2rem)/1.2 ${({ theme }) => theme.font.display};
    letter-spacing: -0.025em;
  }
  & p {
    margin: 0 0 5px;
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
`;
const Filters = styled(Surface)`
  padding: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;
const Search = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  min-height: 38px;
  flex: 1 1 170px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textMuted};
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
  & input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: none;
    color: ${({ theme }) => theme.color.textPrimary};
    font: inherit;
    font-size: ${({ theme }) => theme.fontSize.xs};
    padding: 9px 0;
  }
`;
const Categories = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Category = styled.button<{ $active: boolean }>`
  ${focusRing} border: 1px solid ${({ theme, $active }) =>
    $active ? theme.color.brandFrom : theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 9px 13px;
  min-height: 38px;
  background: ${({ theme, $active }) => ($active ? theme.color.brandFrom : theme.color.surface)};
  color: ${({ theme, $active }) => ($active ? theme.color.onAccent : theme.color.textPrimary)};
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.xs};
  cursor: pointer;
  &:hover {
    filter: brightness(1.12);
  }
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;
const Empty = styled(Surface)`
  padding: 36px 20px;
  text-align: center;
  & h2 {
    font-size: 1rem;
  }
  & button {
    margin-top: 16px;
  }
`;
const Results = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  & button {
    min-height: 30px;
    padding: 5px 10px;
  }
`;

export function UpdateLogsContent({ releases = UPDATE_RELEASES }: { releases?: UpdateRelease[] }) {
  const latest = releases[0];
  const location = useLocation();
  const { prefs } = usePreferences();
  const hashVersion = location.hash.replace('#release-', '');
  const [filters, setFilters] = useState<ReleaseFilters>(EMPTY_RELEASE_FILTERS);
  const [open, setOpen] = useState(() => new Set([latest?.version ?? '', hashVersion]));
  const [target, setTarget] = useState<{ version: string } | null>(() =>
    releases.some((release) => release.version === hashVersion) ? { version: hashVersion } : null,
  );
  const history = useRef<HTMLDivElement>(null);
  const visible = filterReleases(releases, filters);
  const filtered =
    filters.search.trim() ||
    filters.version !== 'all' ||
    filters.platform !== 'all' ||
    filters.category !== 'all';
  useEffect(() => {
    if (!target) return;
    const heading = history.current?.querySelector<HTMLButtonElement>(
      `[id="release-${target.version}-heading"]`,
    );
    if (heading) {
      heading.focus({ preventScroll: true });
      heading.scrollIntoView?.({
        behavior:
          prefs.reduceMotion || window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'instant'
            : 'smooth',
        block: 'start',
      });
    }
  }, [target, prefs.reduceMotion]);
  function toggle(version: string) {
    setOpen((previous) => {
      const next = new Set(previous);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
    setTarget(null);
  }
  function viewLatest() {
    if (!latest) return;
    setFilters(EMPTY_RELEASE_FILTERS);
    setOpen((previous) => new Set([...previous, latest.version]));
    // Focus/scroll after React commits the cleared filters and opened release.
    setTarget({ version: latest.version });
  }
  if (!latest)
    return (
      <Empty>
        <h1>Update Logs</h1>
        <Muted>No published release notes are available yet.</Muted>
      </Empty>
    );
  return (
    <Grid>
      <Main>
        <Header>
          <h1>Update Logs</h1>
          <p>Updates, improvements, and what’s coming next.</p>
          <Muted>See what’s new in TruXSocial and explore the full history of releases.</Muted>
        </Header>
        <ReleaseBanner release={latest} onViewRelease={viewLatest} />
        <Filters aria-label="Filter release notes">
          <Search>
            <Icon name="search" size={18} />
            <input
              type="search"
              aria-label="Search release notes"
              placeholder="Search release notes…"
              value={filters.search}
              onChange={(event) => setFilters({ ...filters, search: event.target.value })}
            />
          </Search>
          <Select
            aria-label="Release version"
            value={filters.version}
            onChange={(event) => setFilters({ ...filters, version: event.target.value })}
          >
            <option value="all">All versions</option>
            {releases.map((release) => (
              <option key={release.version} value={release.version}>
                v{release.version}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Release platform"
            value={filters.platform}
            onChange={(event) =>
              setFilters({ ...filters, platform: event.target.value as ReleasePlatform | 'all' })
            }
          >
            <option value="all">All platforms</option>
            {Object.entries(PLATFORM_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </Select>
          <Categories aria-label="Release categories">
            <Category
              type="button"
              $active={filters.category === 'all'}
              aria-pressed={filters.category === 'all'}
              onClick={() => setFilters({ ...filters, category: 'all' })}
            >
              All
            </Category>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <Category
                key={key}
                type="button"
                $active={filters.category === key}
                aria-pressed={filters.category === key}
                onClick={() => setFilters({ ...filters, category: key as ReleaseCategory })}
              >
                {label}
              </Category>
            ))}
          </Categories>
        </Filters>
        {filtered && (
          <Results>
            <Muted role="status">
              {visible.length} {visible.length === 1 ? 'release' : 'releases'} found
            </Muted>
            <Control type="button" onClick={() => setFilters(EMPTY_RELEASE_FILTERS)}>
              Clear filters
            </Control>
          </Results>
        )}
        <List ref={history} aria-label="Release history">
          {visible.map((release) => (
            <ReleaseCard
              key={release.version}
              release={release}
              open={open.has(release.version)}
              onToggle={() => toggle(release.version)}
            />
          ))}
        </List>
        {!visible.length && (
          <Empty>
            <Icon name="search" size={26} />
            <h2>No matching releases</h2>
            <Muted>Try another search or clear your filters to explore the full history.</Muted>
            <Control type="button" onClick={() => setFilters(EMPTY_RELEASE_FILTERS)}>
              Show all releases
            </Control>
          </Empty>
        )}
        <Muted>
          Platform and category filters use recorded release labels. Unclassified older releases
          remain available under All.
        </Muted>
      </Main>
      <ReleaseSidebar releases={releases} latest={latest} />
    </Grid>
  );
}
